import { Signal, Type } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Selector for opt-in markup on content that PrimeNG (or similar) teleports to `document.body`.
 * {@link OmniSearchComponent} treats clicks matching this via `composedPath()` as inside the omni UI
 * so outside-dismiss does not run while the user interacts with the overlay.
 *
 * @remarks Apply the attribute on a wrapper inside the portaled overlay (for example the panel body
 * under `p-popover` with `[appendTo]="'body'"`).
 */
export const OMNI_SEARCH_PORTALED_OVERLAY_SELECTOR = '[data-core-omni-search-portal]';

export interface OmniSearchResult<T = any> {
  title: string;
  description?: string;
  data: T;
}

export interface OmniSearchResultComponent<T = any> {
  data: T;
  providerId: string;
}

/** One persisted omni search for optional provider history UIs. */
export interface OmniSearchHistoryEntry<T = unknown> {
  id: string;
  query: string;
  results: OmniSearchResult<T>[];
  recordedAt: number;
  /** Hit count from when the search was run (may omit on older stored payloads). */
  resultCount?: number;
}

/** Provider-supplied history: entries signal + record/remove (e.g. localStorage-backed). */
export interface OmniSearchHistoryAttachment<T = unknown> {
  entries: Signal<OmniSearchHistoryEntry<T>[]>;
  recordSearch(query: string, results: OmniSearchResult<T>[]): void;
  removeEntry(id: string): void;
}

export interface OmniSearchProvider<T = any> {
  id: string;
  label: string; // e.g., "Search for client"
  description?: string; // hover/help tooltip text
  rolesAllowed: string[];
  subApp: string; // e.g., "BrightNav"
  shortcut?: string; // e.g., "@client", "/conversation"
  search(query: string): Observable<OmniSearchResult<T>[]>;
  loadComponent: () => Promise<Type<OmniSearchResultComponent<T>>>;
  /** When set, omni UI shows recent searches until a search completes (see omni component rules). */
  searchHistory?: OmniSearchHistoryAttachment<T>;
}
